import { Component, OnDestroy, OnInit } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { LocalDataSource } from 'ng2-smart-table';
import { NbDialogService } from '@nebular/theme';
import { UserRolesService, UserService } from '../../../shared/services';
import { UsersDetailComponent } from './users-detail.component';
import { ModalConfirmComponent } from '../../../shared/components/modals/confirm/modal-confirm.component';
import { ManageUsersService } from './manage-users.service';
import { CompaniesService } from '../companies/companies.service';
import { Subscription } from 'rxjs';
import { find, orderBy } from 'lodash';
import { Helper } from '../../../shared/helpers/helper';
import { ThemesSettingsService } from '../../../shared/services/themes-settings.service';
import { ENUM_THEMES } from '../../../shared/helpers/app.constants';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss']
})
export class UsersComponent implements OnInit, OnDestroy {
  userRole = null;
  currentUser = null;
  tableSettings = {
    mode: 'external',
    noDataMessage: null,
    actions: {
      add: false,
      edit: true,
      columnTitle: 'Action'
    },
    edit: {
      editButtonContent: '<i class="icon-edit app-main-color-fg app-font-size-normal"></i>',
      saveButtonContent: '<i class="icon-check  app-main-color-fg app-font-size-normal"></i>',
      cancelButtonContent: '<i class="icon-cancel app-danger-color-fg app-font-size-normal"></i>',
    },
    delete: {
      deleteButtonContent: '<i class="icon-delete app-danger-color-fg app-font-size-normal"></i>',
      confirmDelete: true,
    },
    hideSubHeader: true,
    columns: {
      userName: {
        title: 'Username',
        type: 'string',
        width: '20%',
        sort: false,
      },
      firstName: {
        title: 'Firstname',
        type: 'string',
        sort: false,
        width: '15%',
      },
      lastName: {
        title: 'Lastname',
        type: 'string',
        sort: false,
        width: '15%',
      },
      role: {
        title: 'Role',
        type: 'string',
        width: '20%',
        sort: false,
        valuePrepareFunction: (cell) => {
          if (typeof cell === 'string' && cell.toLowerCase() === 'carbo admin') {
            cell = 'ADMIN';
          }

          return this.titleCasePipe.transform(cell);
        }
      },
      email: {
        title: 'Email',
        type: 'string',
        width: '30%',
        sort: false,
      },
    },
    pager: {
      perPage: 24
    }
  };
  pageState = {
    loading: false,
    selectedCompany: null,
    currentCompanyId: 0,
    isShowCompanySelector: false,
    totalCount: null,
    maxUser: null,
    isDisableAdd: false
  };

  dataSource: any = new LocalDataSource();


  listCompanies: any[] = [];
  selectedListCompanies: any[] = [];

  listRoles: { name: string; value: string; }[];

  subscriptions: Subscription[] = [];

  isDarkTheme: boolean;

  constructor(
    private nbDialogService: NbDialogService,
    private manageUsersService: ManageUsersService,
    private companiesService: CompaniesService,
    private userRolesService: UserRolesService,
    private userService: UserService,
    private titleCasePipe: TitleCasePipe,
    private themesSettingsService: ThemesSettingsService
  ) {
    this.onCheckDarkThemes();
  }

  private onCheckDarkThemes(): void {
    const localData = this.themesSettingsService.getLocalStorage();
    if (localData && localData.themes === ENUM_THEMES.Dark) {
      this.isDarkTheme = true;
      if (document.body.classList.value.toString().indexOf('frac-dark-theme') === -1) document.body.classList.add('frac-dark-theme');
    } else {
      this.isDarkTheme = false;
      document.body.classList.remove('frac-dark-theme');
    }
  }

  ngOnInit() {
    this.pageState.loading = true;
    this.subscriptions.push(
      this.userService.getCurrentUser().subscribe((response) => {
        console.log("response.result===>", response.result)
        if (response && response.result) {
          const user = response.result;
          this.currentUser = user;
          const userRole = this.userRolesService.toUserRole(user);
          if (userRole && typeof userRole === 'string') {
            this.userRole = userRole.toLowerCase();
            // if carbo admin the hide company selector
            if (this.userRole && this.userRolesService.isAdmin(userRole)) {
              if (this.userRolesService.isCarboAdmin(userRole)) {
                this.pageState.isShowCompanySelector = true;
                // Get list companies
                this.getListCompanies().then(companies => {
                  this.listCompanies = orderBy(companies, item => item.name.toLowerCase());
                  const carboAdmin = [{id: 0, name: 'Carbo Company'}];
                  this.listCompanies = carboAdmin.concat(this.listCompanies);
                  this.selectedListCompanies = carboAdmin;
                });
              }
              // load data by role
              this.loadData(this.userRole);
              // get list roles on user detail
              this.listRoles = this.getListRolesByUserRole(userRole);
              // set max user count of the company that user belong to
              if (user && user.maxUser) {
                this.pageState.maxUser = user.maxUser;
              }
            }
          }
        }
      }, (error) => {
        this.pageState.loading = false;
      })
    );
  }

  ngOnDestroy(): void {
    Helper.unsubscribe(this.subscriptions);
  }

  loadData(userRole: string, companyId?: any) {
    this.pageState.loading = true;
    // get list users
    this.subscriptions.push(
      this.manageUsersService.getUsers(userRole, companyId).subscribe((response) => {
        if (response && response.result) {
          let data = response.result.items.map((item) => {
            item.role = item.roleNames[0];
            return item;
          });
          // sort by id desc
          data = data.sort((leftItem, rightItem) => {
            if (leftItem.id > rightItem.id) {
              return -1;
            }
            return 1;
          });

          this.dataSource.load(data);
        }
        this.pageState.totalCount = response.result.totalCount;
        this.pageState.loading = false;
        this.pageState.isDisableAdd = this.checkIsDisableAdd();
      }, (err) => {
        this.pageState.loading = false;
      })
    );
  }

  reloadData(userRole, currentCompanyId) {
    if (userRole && typeof userRole === 'string') {
      this.loadData(userRole, currentCompanyId);
    }
  }

  async getListCompanies() {
    try {
      const response = await this.companiesService.getListCompany().toPromise();
      if (response && response.result && response.result.items) {
        return response.result.items;
      }
      return [];
    } catch (err) {
      throw err;
    }
  }

  getListRolesByUserRole(userRole) {
    let listRoles = this.userRolesService.getRoles();
    if (typeof userRole === 'string') {
      if (userRole.toLowerCase() === 'carbo admin') {
        listRoles = listRoles.filter(role => role.value !== 'Admin');
      } else {
        listRoles = listRoles.filter(role => role.value !== 'Carbo Admin');
      }
    }
    return listRoles;
  }

  onCreate(): void {
    this.nbDialogService.open(UsersDetailComponent, {
      autoFocus: false,
      context: {
        listRoles: this.listRoles,
        userRole: this.userRole,
        currentCompanyId: this.pageState.currentCompanyId ? this.pageState.currentCompanyId : ''
      }
    })
      .onClose.subscribe(data => {
        if (data) {
          this.reloadData(this.userRole, this.pageState.currentCompanyId);
        }
      });
  }

  onEdit(event): void {
    if (event && event.data && event.data.id) {
      const userId = event.data.id;
      this.manageUsersService.getUserDetail(this.userRole, userId).then(userData => {
        userData.id = userId;
        this.nbDialogService.open(UsersDetailComponent, {
          autoFocus: false,
          context: {
            listRoles: this.listRoles,
            userRole: this.userRole,
            currentCompanyId: this.pageState.currentCompanyId ? this.pageState.currentCompanyId : '',
            detail: userData,
            currentUser: this.currentUser
          }
        })
          .onClose.subscribe(data => {
            if (data) {
              this.reloadData(this.userRole, this.pageState.currentCompanyId);
            }
          });
      });
    }
  }

  onDelete(event): void {
    this.nbDialogService.open(ModalConfirmComponent, {
      context: {
        config: {
          title: 'Delete',
          message: `Are you sure you want to delete <br> <b class="h6">${event.data.userName}</b>?`,
          className: 'pop-delete common-page',
          btnTxtConfirm: 'Yes',
          btnTxtCancel: 'No',
        }
      }
    })
      .onClose.subscribe(response => {
        if (response) {
          if (response.confirm && event.data && event.data.id) {
            this.handleDelete(event.data.id);
          }
        }
      });
  }

  // Change company only available for Carbo Admin
  onChangeCompany(company) {
    if (this.pageState.currentCompanyId !== company.id) {
      if (company && company.id) {
        const result = find(this.listCompanies, {id: company.id});
        this.pageState.selectedCompany = result;
        this.pageState.currentCompanyId = result.id;
        this.listRoles = this.getListRolesByUserRole('Admin');
      } else {
        this.pageState.currentCompanyId = 0;
        this.pageState.selectedCompany = null;
        this.listRoles = this.getListRolesByUserRole('Carbo Admin');
      }
      this.loadData(this.userRole, this.pageState.currentCompanyId);
    }
  }

  handleDelete(id) {
    // call api delete user record
    if (id) {
      this.subscriptions.push(
        this.manageUsersService.deleteUser(this.userRole, id, () => {
          this.reloadData(this.userRole, this.pageState.currentCompanyId);
        }).subscribe((res) => {
        })
      );
    }
  }

  checkIsDisableAdd() {
    if (this.pageState) {
      if (this.pageState.selectedCompany) {
        return this.pageState.totalCount >= this.pageState.selectedCompany.maxUser;
      } else if (this.pageState.maxUser) {
        return this.pageState.totalCount >= this.pageState.maxUser;
      }
    }
    return false;
  }
}
